/**
 * The four controlled vocabularies, and their labels in the three product
 * languages.
 *
 * These mirror §8 of the master instruction verbatim. They are declared here
 * rather than imported from a page module so the design system stays a leaf:
 * nothing in `components/legenda` may depend on a screen.
 *
 * The vocabularies are deliberately *not* merged into one `status` union. They
 * describe four different nouns — a document, a number, a whole view, a
 * position — and the visual system gives each its own channel for exactly that
 * reason. Collapsing them here would invite a component that renders one badge
 * for all four, which is the failure the design exists to prevent.
 */

export type LegendaLang = 'ru' | 'hy' | 'en';

/** How much a specific number is believed. Attaches to a value. */
export type Verification =
  | 'verified'
  | 'reviewed_supported'
  | 'high_confidence_candidate'
  | 'medium_confidence_candidate'
  | 'low_confidence_candidate'
  | 'conflict'
  | 'unknown'
  | 'rejected';

/** The physical state of a document. Attaches to a source file. */
export type SourceIntegrity =
  | 'complete'
  | 'truncated_recovered'
  | 'corrupt'
  | 'missing'
  | 'duplicate_exact'
  | 'superseded';

/** Whether a body of facts may be treated as truth. Attaches to a whole view. */
export type PublicationState =
  | 'draft'
  | 'provisional_only'
  | 'in_review'
  | 'published'
  | 'retracted'
  | 'superseded';

/** Four genuinely different kinds of "where". Attaches to a position. */
export type CoordinateRole =
  | 'raw_local'
  | 'candidate_transformation'
  | 'cross_document_orientation'
  | 'survey_certified';

type Labels<T extends string> = Record<T, Record<LegendaLang, string>>;

export const VERIFICATION_LABELS: Labels<Verification> = {
  verified: { ru: 'проверено', hy: 'հաստատված', en: 'verified' },
  reviewed_supported: {
    ru: 'подтверждено источником',
    hy: 'աղբյուրով հիմնավորված',
    en: 'supported by source',
  },
  high_confidence_candidate: {
    ru: 'кандидат · высокая',
    hy: 'թեկնածու · բարձր',
    en: 'candidate · high',
  },
  medium_confidence_candidate: {
    ru: 'кандидат · средняя',
    hy: 'թեկնածու · միջին',
    en: 'candidate · medium',
  },
  low_confidence_candidate: {
    ru: 'кандидат · низкая',
    hy: 'թեկնածու · ցածր',
    en: 'candidate · low',
  },
  conflict: { ru: 'конфликт', hy: 'հակասություն', en: 'conflict' },
  unknown: { ru: 'нет данных', hy: 'տվյալներ չկան', en: 'no data' },
  rejected: { ru: 'отклонено', hy: 'մերժված', en: 'rejected' },
};

export const INTEGRITY_LABELS: Labels<SourceIntegrity> = {
  complete: { ru: 'целостный', hy: 'ամբողջական', en: 'complete' },
  truncated_recovered: { ru: 'оборван', hy: 'կտրված', en: 'truncated' },
  corrupt: { ru: 'повреждён', hy: 'վնասված', en: 'corrupt' },
  missing: { ru: 'отсутствует', hy: 'բացակայում է', en: 'missing' },
  duplicate_exact: { ru: 'дубликат', hy: 'կրկնօրինակ', en: 'duplicate' },
  superseded: { ru: 'заменён', hy: 'փոխարինված', en: 'superseded' },
};

export const PUBLICATION_LABELS: Labels<PublicationState> = {
  draft: { ru: 'черновик', hy: 'սևագիր', en: 'draft' },
  provisional_only: { ru: 'предварительные данные', hy: 'նախնական տվյալներ', en: 'provisional data' },
  in_review: { ru: 'на рецензии', hy: 'վերանայման փուլում', en: 'in review' },
  published: { ru: 'опубликовано', hy: 'հրապարակված', en: 'published' },
  retracted: { ru: 'отозвано', hy: 'հետ կանչված', en: 'retracted' },
  superseded: { ru: 'заменено', hy: 'փոխարինված', en: 'superseded' },
};

export const COORDINATE_ROLE_LABELS: Labels<CoordinateRole> = {
  raw_local: { ru: 'рудничная сетка', hy: 'հանքային ցանց', en: 'local mine grid' },
  candidate_transformation: {
    ru: 'кандидатная привязка',
    hy: 'թեկնածու փոխակերպում',
    en: 'candidate transformation',
  },
  cross_document_orientation: {
    ru: 'ориентир из документа',
    hy: 'կողմնորոշիչ կետ',
    en: 'cross-document orientation',
  },
  survey_certified: { ru: 'съёмка', hy: 'գեոդեզիական չափագրում', en: 'survey certified' },
};

/** Every publication state except the one that may be shown unmarked. */
export type MarkedPublicationState = Exclude<PublicationState, 'published'>;

/**
 * `published` is the only state presented without hazard chrome.
 *
 * Written as a type predicate rather than a set membership test so the banner
 * narrows on it: a ribbon can only be rendered for a state that actually has
 * ribbon text, and adding a seventh publication state would fail to compile
 * until its wording exists. Clean chrome is reserved for ground truth, and
 * nothing else can drift into looking like it.
 */
export function requiresPublicationChrome(state: PublicationState): state is MarkedPublicationState {
  return state !== 'published';
}

/**
 * The verification ramp, in order, for the confidence meter.
 *
 * Only the five degrees of belief appear. `conflict`, `unknown` and `rejected`
 * are categorical and have no position on a scale — rendering them as a notch
 * would state that a conflict is a *level* of confidence, which it is not.
 */
export const CONFIDENCE_RAMP = [
  'verified',
  'reviewed_supported',
  'high_confidence_candidate',
  'medium_confidence_candidate',
  'low_confidence_candidate',
] as const satisfies readonly Verification[];

/** Index in `CONFIDENCE_RAMP` after which the value is machine-estimated. */
export const MEASURED_ESTIMATED_BOUNDARY = 2;

/**
 * Where a borehole may be drawn.
 *
 * `raw_local` is absent by design. A Soviet mine-grid coordinate has no
 * defensible global position, so it never reaches a WGS84 layer — see
 * `isMappableInWgs84`, which the map layer uses as a type guard rather than an
 * `if`, so an unplaceable collar cannot be passed to it by mistake.
 */
export type MappableCoordinateRole = Exclude<CoordinateRole, 'raw_local'>;

export function isMappableInWgs84(role: CoordinateRole): role is MappableCoordinateRole {
  return role !== 'raw_local';
}

const LOCALE_BY_LANG: Record<LegendaLang, string> = {
  ru: 'ru-RU',
  hy: 'hy-AM',
  en: 'en-GB',
};

/**
 * Format a measured quantity for display.
 *
 * The decimal separator follows the locale; the digits themselves stay Latin in
 * every language, as do units, CRS identifiers and hashes — those are formatted
 * by the callers that own them, never by this function.
 */
export function formatQuantity(
  value: number,
  lang: LegendaLang,
  fractionDigits?: number,
): string {
  return new Intl.NumberFormat(LOCALE_BY_LANG[lang], {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits ?? 3,
    useGrouping: true,
  }).format(value);
}
